import { useEffect, useState } from 'react';

const MAX_PRESENTED_MEDIA_CLOCK_SKEW_MS = 500;

type ResolvePresentedCaptionTimeInput = {
  sequenceTimeMs: number;
  sequenceStartMs: number;
  presentedMediaTimeMs: number | null;
};

export const resolvePresentedCaptionTimeMs = ({
  sequenceTimeMs,
  sequenceStartMs,
  presentedMediaTimeMs,
}: ResolvePresentedCaptionTimeInput): number => {
  if (presentedMediaTimeMs == null || !Number.isFinite(presentedMediaTimeMs)) {
    return sequenceTimeMs;
  }

  const expectedMediaTimeMs = sequenceStartMs + sequenceTimeMs;
  if (
    Math.abs(presentedMediaTimeMs - expectedMediaTimeMs) >
    MAX_PRESENTED_MEDIA_CLOCK_SKEW_MS
  ) {
    return sequenceTimeMs;
  }

  return Math.max(0, presentedMediaTimeMs - sequenceStartMs);
};

const findCaptionClockSourceVideo = (): HTMLVideoElement | null => {
  if (typeof document === 'undefined') return null;

  return (
    Array.from(
      document.querySelectorAll<HTMLVideoElement>(
        '.__remotion-player video[data-editor-caption-clock-source="true"]',
      ),
    ).find((video) => !video.muted && video.readyState >= 2) ?? null
  );
};

export const usePresentedCaptionTimeMs = ({
  enabled,
  sequenceTimeMs,
  sequenceStartMs,
}: {
  enabled: boolean;
  sequenceTimeMs: number;
  sequenceStartMs: number;
}): number => {
  const [presentedMediaTimeMs, setPresentedMediaTimeMs] = useState<
    number | null
  >(null);

  useEffect(() => {
    if (!enabled) {
      setPresentedMediaTimeMs(null);
      return;
    }

    let cancelled = false;
    let animationFrameId: number | null = null;
    let videoFrameCallbackId: number | null = null;
    let subscribedVideo: HTMLVideoElement | null = null;
    let pendingPlayVideo: HTMLVideoElement | null = null;
    const resetPresentedMediaTime = () => setPresentedMediaTimeMs(null);

    const handlePendingPlay = () => {
      pendingPlayVideo?.removeEventListener('play', handlePendingPlay);
      pendingPlayVideo = null;
      subscribe();
    };

    function subscribe() {
      if (cancelled) return;

      const video = findCaptionClockSourceVideo();
      if (!video || typeof video.requestVideoFrameCallback !== 'function') {
        animationFrameId = window.requestAnimationFrame(subscribe);
        return;
      }
      if (video.paused) {
        pendingPlayVideo = video;
        video.addEventListener('play', handlePendingPlay, { once: true });
        return;
      }

      subscribedVideo = video;
      video.addEventListener('pause', resetPresentedMediaTime);
      video.addEventListener('seeking', resetPresentedMediaTime);
      video.addEventListener('ended', resetPresentedMediaTime);
      video.addEventListener('emptied', resetPresentedMediaTime);
      const onVideoFrame: VideoFrameRequestCallback = (_now, metadata) => {
        if (cancelled) return;
        setPresentedMediaTimeMs(Math.round(metadata.mediaTime * 1000));
        videoFrameCallbackId = video.requestVideoFrameCallback(onVideoFrame);
      };
      videoFrameCallbackId = video.requestVideoFrameCallback(onVideoFrame);
    }

    subscribe();

    return () => {
      cancelled = true;
      if (animationFrameId != null) {
        window.cancelAnimationFrame(animationFrameId);
      }
      if (pendingPlayVideo) {
        pendingPlayVideo.removeEventListener('play', handlePendingPlay);
      }
      if (
        subscribedVideo &&
        videoFrameCallbackId != null &&
        typeof subscribedVideo.cancelVideoFrameCallback === 'function'
      ) {
        subscribedVideo.cancelVideoFrameCallback(videoFrameCallbackId);
      }
      if (subscribedVideo) {
        subscribedVideo.removeEventListener('pause', resetPresentedMediaTime);
        subscribedVideo.removeEventListener('seeking', resetPresentedMediaTime);
        subscribedVideo.removeEventListener('ended', resetPresentedMediaTime);
        subscribedVideo.removeEventListener('emptied', resetPresentedMediaTime);
      }
    };
  }, [enabled]);

  return resolvePresentedCaptionTimeMs({
    sequenceTimeMs,
    sequenceStartMs,
    presentedMediaTimeMs: enabled ? presentedMediaTimeMs : null,
  });
};
